import React, { useState } from 'react';
import { CompetitionType } from 'src/domains/layouts/webview/components/promotedSports/PromotedEventsState.state';
import { observer } from 'src/utils/mobx-react';
import { EventRowMobxWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx';
import { EventRow } from 'src/domains/sportsbook/webview/components/eventRow/EventRow';
import { EventRowNFL } from 'src/domains/sportsbook/shared/Components';
import { EventRowMLB } from 'src/domains/sportsbook/webview/components/eventsList/eventRowMLB/EventRowMLB';
import { EventRowNBA } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNBA/EventRowNBA';
import { EventRowNHL } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNHL/EventRowNHL';
import { makeObservable, observable } from 'mobx';
import {
    getMarketAndSelections,
    PossibleMarketAndSelectionInListType,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/getMarketAndSelections';
import { SELECTIONS_LABELS } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventGroupHeaderMLB } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderMLB';
import { EventGroupHeaderNBA } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNBA';
import { EventGroupHeaderNFL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNFL';
import { EventGroupHeaderNHL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNHL';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { CompetitionHeader } from 'src/domains/sportsbook/webview/components/competitionHeader/CompetitionHeader';
import { CompetitionWrapper } from './PromotedSportSection.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventRowUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowUnified';
import { EventHeader } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeaderUnified';
import { SELECTIONS_LABELS as SELECTIONS_LABELS_UNIFIED } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';
import { EventRowOutrightsUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowOutrightsUnified';
import {
    getAmericanLeagueRowFlags,
    getAmericanSportLogoStateConstructor,
    shouldUseUnifiedAmericanSportRow,
} from 'src/domains/sportsbook/webview/components/eventsList/americanSportEventRow';
import { isOutrightEventItem, isValidEventItem } from 'src/domains/sportsbook/utils/isOutrightEvent';

interface CompetitionDataPropsType {
    competition: CompetitionType;
    sportSlug: string;
}

const renderClassicAmericanSportRow = (
    event: EventListGroupEventItemType,
    flags: ReturnType<typeof getAmericanLeagueRowFlags>
): JSX.Element | null => {
    const { id2, id, template } = event;
    const isTwoParticipant = template.startsWith('two-participant');

    if (flags.isBaseball === true && isTwoParticipant) {
        return (
            <EventRowMLB
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
                showParticipantLogos={flags.isMLBorMajorCompetition}
            />
        );
    }

    if (flags.isNHL === true && isTwoParticipant) {
        return (
            <EventRowNHL
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    }

    if (flags.isNBA === true && isTwoParticipant) {
        return (
            <EventRowNBA
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    }

    if (flags.isNFL === true) {
        return (
            <EventRowNFL
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    }

    return null;
};

const renderCompetitionView = (
    competitionId: CompetitionId,
    event: EventListGroupEventItemType,
    sportSlug: string,
    possibleMarketAndSelectionInList: PossibleMarketAndSelectionInListType
): JSX.Element => {
    const competitionName = competitionId.getModel()?.name ?? '';
    const { id } = event;
    const common = useCommon();
    const { geniusSports, unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;
    const flags = getAmericanLeagueRowFlags(competitionName, sportSlug);
    const classicAmericanRow = renderClassicAmericanSportRow(event, flags);

    if (classicAmericanRow !== null) {
        if (
            shouldUseUnifiedAmericanSportRow(flags, sportSlug, geniusSports) === true &&
            unifiedSportEventsTable === true
        ) {
            return (
                <EventRowUnified
                    key={`event_${event.id}`}
                    sportId={sportSlug}
                    smallEvent={event}
                    selectionsHeaders={possibleMarketAndSelectionInList.headers}
                    competitionId={competitionId}
                    rowStateConstructor={getAmericanSportLogoStateConstructor(flags, event)}
                />
            );
        }

        return classicAmericanRow;
    }

    if (unifiedSportEventsTable && isOutrightEventItem(event)) {
        return (
            <EventRowOutrightsUnified
                key={`event_${event.id}`}
                smallEvent={event}
            />
        );
    }

    if (sportSlug === 'football') {
        return unifiedSportEventsTable ? (
            <EventRowUnified
                key={`event_${event.id}`}
                sportId={sportSlug}
                smallEvent={event}
                selectionsHeaders={possibleMarketAndSelectionInList.headers}
                competitionId={competitionId}
            />
        ) : (
            <EventRowMobxWrapper
                key={`event_${id}`}
                smallEvent={event}
                competitionId={competitionId}
            />
        );
    }

    return unifiedSportEventsTable ? (
        <EventRowUnified
            key={`event_${event.id}`}
            sportId={sportSlug}
            smallEvent={event}
            selectionsHeaders={possibleMarketAndSelectionInList.headers}
            competitionId={competitionId}
        />
    ) : (
        <EventRow
            key={`event_${event.id}`}
            sport={sportSlug}
            smallEvent={event}
            possibleMarketAndSelectionInList={possibleMarketAndSelectionInList}
            filterMarketsByPopularTag={false}
        />
    );
};

interface HeaderType {
    competition: CompetitionType;
    isOpenedCompetition: boolean;
    sportSlug: string;
    setIsOpened: () => void;
    smallEventsArray: Array<EventListGroupEventItemType>;
}

const Header = observer(
    'Header',
    ({ competition, isOpenedCompetition, sportSlug, setIsOpened, smallEventsArray }: HeaderType): JSX.Element => {
        const common = useCommon();
        const languagesState = LanguagesState.get(common);
        const { geniusSports } = ConfigComponents.get(common).config;
        const { unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;

        const competitionName = competition.competition_id.getModel()?.name ?? '';
        const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
        const selectionsLabels = (unifiedSportEventsTable ? SELECTIONS_LABELS_UNIFIED : SELECTIONS_LABELS)(
            languagesState.getTranslation
        );
        const selections = possibleMarketAndSelectionInList.headers.map(
            (x) => selectionsLabels[`${sportSlug}:${x}`] ?? selectionsLabels[x] ?? x
        );
        const areEventsTwoParticipantsOnly = smallEventsArray.every((smallEvent) =>
            smallEvent.template.startsWith('two-participant')
        );
        const flags = getAmericanLeagueRowFlags(competitionName, sportSlug);
        const useUnifiedAmericanRow =
            shouldUseUnifiedAmericanSportRow(flags, sportSlug, geniusSports) === true &&
            unifiedSportEventsTable === true;

        if (useUnifiedAmericanRow === false) {
            if (flags.isBaseball === true && areEventsTwoParticipantsOnly === true) {
                return (
                    <EventGroupHeaderMLB
                        key={`sport_competition_${competition.competition_id.toOldId()}`}
                        label={competitionName}
                        onToggle={setIsOpened}
                        open={isOpenedCompetition}
                        dataTest='header-competitionLevel'
                    />
                );
            }

            if (flags.isNHL === true && areEventsTwoParticipantsOnly === true) {
                return (
                    <EventGroupHeaderNHL
                        key={`sport_competition_${competition.competition_id.toOldId()}`}
                        label={competitionName}
                        onToggle={setIsOpened}
                        open={isOpenedCompetition}
                        dataTest='header-competitionLevel'
                    />
                );
            }

            if (flags.isNBA === true && areEventsTwoParticipantsOnly === true) {
                return (
                    <EventGroupHeaderNBA
                        key={`sport_competition_${competition.competition_id.toOldId()}`}
                        label={competitionName}
                        onToggle={setIsOpened}
                        open={isOpenedCompetition}
                        dataTest='header-competitionLevel'
                    />
                );
            }

            if (flags.isNFL === true) {
                return (
                    <EventGroupHeaderNFL
                        dataTest='header-competitionLevel'
                        key={`sport_competition_${competition.competition_id.toOldId()}`}
                        label={competitionName}
                        onToggle={setIsOpened}
                        open={isOpenedCompetition}
                    />
                );
            }
        }

        return unifiedSportEventsTable ? (
            <EventHeader
                selections={selections}
                label={competitionName}
                showCompetitonLabel
                sportId={sportSlug}
            />
        ) : (
            <CompetitionHeader
                selections={selections}
                isOutrightEvent={false}
                label={competitionName}
                isOpen={isOpenedCompetition}
                onToggle={setIsOpened}
            />
        );
    }
);

class State {
    @observable public isOpenedCompetition: boolean = true;

    public constructor() {
        makeObservable(this);
    }

    public setIsOpened = (): void => {
        this.isOpenedCompetition = !this.isOpenedCompetition;
    };
}

export const CompetitionData = observer('CompetitionData', ({ competition, sportSlug }: CompetitionDataPropsType) => {
    const common = useCommon();
    const [state] = useState(() => new State());

    const configState = ConfigComponents.get(common).config;
    const { unifiedSportEventsTable, geniusSports } = configState;
    const applyGeniusOutrightsValidation =
        unifiedSportEventsTable === true && geniusSports.some((sport) => sport === sportSlug);
    const smallEventsArray = competition.events_list
        .map((event) => event.smallEvent)
        .filter((event) => {
            const eventModel = event.id2.getEventModel();
            if (eventModel === null || eventModel.display === false || eventModel.timeSettingsStarted) {
                return false;
            }

            if (eventModel.sport === 'horseracing' || eventModel.sport === 'greyhoundracing') {
                return eventModel.template === 'outrights' || eventModel.antePost;
            }
            return true;
        });
    const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
    const eventsSorted = competition.events_list
        .filter(({ smallEvent }) => !applyGeniusOutrightsValidation || isValidEventItem(smallEvent))
        .sort((a, b) => {
            const aIsOutright = isOutrightEventItem(a.smallEvent);
            const bIsOutright = isOutrightEventItem(b.smallEvent);

            if (aIsOutright && !bIsOutright) return 1;
            if (!aIsOutright && bIsOutright) return -1;

            return a.display_order - b.display_order;
        });

    return eventsSorted.length > 0 ? (
        <React.Fragment key={competition.id}>
            <Header
                competition={competition}
                sportSlug={sportSlug}
                isOpenedCompetition={state.isOpenedCompetition}
                setIsOpened={state.setIsOpened}
                smallEventsArray={smallEventsArray}
            />
            {state.isOpenedCompetition ? (
                <CompetitionWrapper variant={configState.componentVariants.sportsbookVariant}>
                    {eventsSorted.map((eventModel) => {
                        const smallEvent = eventModel.smallEvent;

                        return renderCompetitionView(
                            competition.competition_id,
                            smallEvent,
                            sportSlug,
                            possibleMarketAndSelectionInList
                        );
                    })}
                </CompetitionWrapper>
            ) : null}
        </React.Fragment>
    ) : null;
});
